/**
 * @file shipment-events-view.ts
 * @description Сборка ленты событий доставки для показа (phase19.13 шаг 6).
 *
 * Перевозчик присылает десятки транзитных событий подряд («в пути»,
 * «в пути», «в пути»...) — в сыром виде лента прячет то, ради чего её
 * открывают. Здесь чистые функции: подряд идущие транзиты сворачиваются в
 * одну строку, длинная лента режется до последних событий.
 *
 * Без React намеренно — правила проверяются юнит-тестом, а не скриншотом.
 */

import type { ShipmentEventDto } from "./api-delivery-monitoring";
import { pluralRu } from "./plural";

/** Сколько транзитов подряд уже стоит прятать. */
export const MIN_TRANSIT_GROUP = 3;

/** С какого объёма ленты показываем только хвост. */
export const LONG_FEED_THRESHOLD = 30;

/** Сколько событий показываем у длинной ленты до «Показать все». */
export const LONG_FEED_VISIBLE = 15;

/** Одно событие как отдельная строка ленты. */
export interface ShipmentEventRowSingle {
  kind: "event";
  key: string;
  event: ShipmentEventDto;
}

/** Свёрнутая пачка транзитов. */
export interface ShipmentEventRowTransitGroup {
  kind: "transit";
  key: string;
  events: ShipmentEventDto[];
  /** Город первого по времени события пачки. */
  fromCity: string | null;
  /** Город последнего по времени события пачки. */
  toCity: string | null;
}

export type ShipmentEventRow = ShipmentEventRowSingle | ShipmentEventRowTransitGroup;

/**
 * Хвост ленты для показа.
 *
 * Лента приходит от сервера новыми сверху, поэтому «последние N» — это первые
 * N элементов массива.
 */
export function visibleShipmentEvents(
  items: ShipmentEventDto[],
  showAll: boolean,
): { visible: ShipmentEventDto[]; hiddenCount: number } {
  if (showAll || items.length <= LONG_FEED_THRESHOLD) {
    return { visible: items, hiddenCount: 0 };
  }
  return {
    visible: items.slice(0, LONG_FEED_VISIBLE),
    hiddenCount: items.length - LONG_FEED_VISIBLE,
  };
}

/**
 * События → строки ленты со свёрнутыми транзитами.
 *
 * Самое свежее событие не сворачивается никогда: именно оно отвечает на
 * вопрос «где посылка сейчас», и прятать его за раскрывашку нельзя.
 *
 * @param items события новыми сверху (порядок сервера)
 */
export function buildShipmentEventRows(items: ShipmentEventDto[]): ShipmentEventRow[] {
  const rows: ShipmentEventRow[] = [];
  let index = 0;

  while (index < items.length) {
    const current = items[index];
    if (!current) break;

    // Самое свежее событие ленты — всегда отдельной строкой.
    if (index === 0 || current.stage !== "IN_TRANSIT") {
      rows.push({ kind: "event", key: current.id, event: current });
      index += 1;
      continue;
    }

    let end = index;
    while (end < items.length && items[end]?.stage === "IN_TRANSIT") end += 1;
    const run = items.slice(index, end);

    if (run.length < MIN_TRANSIT_GROUP) {
      for (const event of run) rows.push({ kind: "event", key: event.id, event });
    } else {
      const oldest = run[run.length - 1];
      const newest = run[0];
      rows.push({
        kind: "transit",
        key: `transit-${newest?.id ?? index}`,
        events: run,
        fromCity: oldest?.city ?? null,
        toCity: newest?.city ?? null,
      });
    }
    index = end;
  }

  return rows;
}

/** Подпись свёрнутой пачки: «5 транзитных событий · Челябинск → Москва». */
export function transitGroupLabel(row: ShipmentEventRowTransitGroup): string {
  const count = `${row.events.length} ${pluralRu(row.events.length, [
    "транзитное событие",
    "транзитных события",
    "транзитных событий",
  ])}`;
  if (row.fromCity && row.toCity && row.fromCity !== row.toCity) {
    return `${count} · ${row.fromCity} → ${row.toCity}`;
  }
  const city = row.toCity ?? row.fromCity;
  return city ? `${count} · ${city}` : count;
}

/**
 * Есть ли у события сырой payload для просмотра.
 *
 * `raw` не приходит совсем, если у пользователя нет права разбирать инциденты,
 * и приходит `null`, если перевозчик не прислал тело. В обоих случаях кнопку
 * `[{ }]` показывать не за чем — открылась бы панель со словом «null».
 */
export function hasRawPayload(event: ShipmentEventDto): boolean {
  return event.raw !== undefined && event.raw !== null;
}
